import {
  useState,
} from "react";

import registrationService from "../../../services/registrationService";
import {
  designTokens,
} from "../../../shared/styles/designTokens";

const normalizeEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    String(value || "").trim()
  );

const EmailVerificationGate = ({
  initialName = "",
  initialEmail = "",
  onVerified,
}) => {
  const [name, setName] =
    useState(initialName);

  const [email, setEmail] =
    useState(initialEmail);

  const [otp, setOtp] =
    useState("");

  const [otpSent, setOtpSent] =
    useState(false);

  const [busy, setBusy] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [error, setError] =
    useState("");

  const sendOtp = async () => {
    const cleanName =
      String(name || "").trim();

    const cleanEmail =
      normalizeEmail(email);

    setError("");
    setMessage("");

    if (!cleanName) {
      setError(
        "Please enter your Full Name."
      );
      return;
    }

    if (!isValidEmail(cleanEmail)) {
      setError(
        "Please enter a valid email address."
      );
      return;
    }

    setBusy(true);

    try {
      /*
       * Reuse the existing authoritative availability endpoint.
       * Do not add a second email-existence implementation.
       */
      const availability =
        await registrationService.checkAccountAvailability({
          email: cleanEmail,
        });

      if (!availability?.email) {
        throw new Error(
          "Email availability could not be confirmed. Please try again."
        );
      }

      if (
        availability.email.exists
      ) {
        setError(
          'This email is already registered. Please login with this email and use "Forgot Password" if needed.'
        );
        return;
      }

      await registrationService.sendRegistrationEmailOtp({
        name: cleanName,
        email: cleanEmail,
      });

      setEmail(cleanEmail);
      setOtp("");
      setOtpSent(true);
      setMessage(
        `A 6-digit OTP has been sent to ${cleanEmail}.`
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data
          ?.message ||
          requestError?.message ||
          "Unable to send the verification OTP. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) {
      setError(
        "Enter the 6-digit OTP sent to your email."
      );
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");

    try {
      const response =
        await registrationService.verifyRegistrationEmailOtp({
          email,
          otp,
        });

      if (
        !response?.verificationToken
      ) {
        throw new Error(
          "Email verification completed without a verification token."
        );
      }

      onVerified({
        name:
          String(name || "").trim(),
        email:
          normalizeEmail(email),
        verificationToken:
          response.verificationToken,
      });
    } catch (requestError) {
      setError(
        requestError?.response?.data
          ?.message ||
          requestError?.message ||
          "OTP verification failed. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const changeEmail = () => {
    setOtpSent(false);
    setOtp("");
    setMessage("");
    setError("");
  };

  return (
    <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-white p-6 shadow-sm md:p-8">
      <div className="mb-6">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-amber-700">
          Step 1
        </p>

        <h2
          className="mt-2 text-2xl font-semibold"
          style={{
            color:
              designTokens.colors.primary,
          }}
        >
          Verify Your Email
        </h2>

        <p className="mt-2 text-sm leading-6 text-slate-600">
          We verify your email before opening the full registration form so that profiles are not created with an invalid email address.
        </p>
      </div>

      <div className="space-y-5">
        <div>
          <label
            className="mb-1.5 block text-sm font-medium text-slate-700"
            htmlFor="registration-name"
          >
            Full Name
          </label>

          <input
            id="registration-name"
            type="text"
            value={name}
            disabled={busy}
            onChange={(event) =>
              setName(
                event.target.value
              )
            }
            className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
            placeholder="Enter your full name"
          />
        </div>

        <div>
          <label
            className="mb-1.5 block text-sm font-medium text-slate-700"
            htmlFor="registration-email"
          >
            Email ID
          </label>

          <div className="flex gap-2">
            <input
              id="registration-email"
              type="email"
              value={email}
              disabled={
                busy || otpSent
              }
              onChange={(event) =>
                setEmail(
                  event.target.value
                )
              }
              className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
              placeholder="name@example.com"
            />

            {otpSent && (
              <button
                type="button"
                onClick={changeEmail}
                disabled={busy}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Change Email
              </button>
            )}
          </div>
        </div>

        {!otpSent ? (
          <button
            type="button"
            onClick={sendOtp}
            disabled={busy}
            className="w-full rounded-xl bg-amber-700 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy
              ? "Checking & Sending..."
              : "Send Verification OTP"}
          </button>
        ) : (
          <>
            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-slate-700"
                htmlFor="registration-otp"
              >
                Verification OTP
              </label>

              <input
                id="registration-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                disabled={busy}
                onChange={(event) =>
                  setOtp(
                    event.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6)
                  )
                }
                className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-center text-lg font-semibold tracking-[0.32em] outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:bg-slate-50"
                placeholder="000000"
              />
            </div>

            <button
              type="button"
              onClick={verifyOtp}
              disabled={busy}
              className="w-full rounded-xl bg-amber-700 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy
                ? "Verifying..."
                : "Verify & Continue"}
            </button>

            <button
              type="button"
              onClick={sendOtp}
              disabled={busy}
              className="w-full rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Resend OTP
            </button>
          </>
        )}

        {message && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {message}
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <p className="text-center text-xs leading-5 text-slate-500">
          Your full registration form will open only after successful email verification.
        </p>
      </div>
    </div>
  );
};

export default EmailVerificationGate;
